// Protected 9:16 player.
// - With a signed HLS URL it plays real video (native HLS on Safari/iOS, hls.js elsewhere when loaded),
//   with download, picture-in-picture and casting turned off.
// - In mock mode there is no video file, so it plays a timed "frame" that behaves the same way
//   (progress, pause on tab switch, heartbeat, end of episode).
// Anti-piracy: optional moving viewer code (no personal details), pause when the tab is hidden / dev tools open / screenshot key.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { BLOCK_TEXT, usePlaybackBlock } from '../lib/antipiracy';
import { clock } from '../lib/format';
import { setState, useApp, type Quality } from '../lib/store';
import { Icon } from './Icon';
import { haptic } from '../lib/haptics';

declare global {
  interface Window { Hls?: { isSupported(): boolean; new (): { loadSource(u: string): void; attachMedia(v: HTMLVideoElement): void; destroy(): void } } }
}

const SPOTS = [
  { left: '8%', top: '22%' }, { right: '8%', top: '40%' }, { left: '12%', top: '62%' }, { right: '10%', top: '14%' },
];

/** Moves every 7 s: fades out, jumps to the next spot, fades back in (no sliding across the video). */
export function Watermark({ text }: { text: string }) {
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(true);
  useEffect(() => {
    let hop = 0;
    const t = window.setInterval(() => {
      setShown(false);
      hop = window.setTimeout(() => { setI((n) => (n + 1) % SPOTS.length); setShown(true); }, 400);
    }, 7000);
    return () => { window.clearInterval(t); window.clearTimeout(hop); };
  }, []);
  return <span className="watermark" style={{ ...(SPOTS[i] as CSSProperties), opacity: shown ? undefined : 0 }} aria-hidden="true">{text}</span>;
}

const QUALITY_LABEL: Record<Quality, string> = { auto: 'Auto', saver: '360p', hd: '720p' };
const SPEEDS = [1, 1.25, 1.5, 2, 0.75];

// hls.js is loaded only when a browser can't play HLS itself (Chrome/Firefox on desktop and Android WebView).
declare const __HLS_URL__: string;
let hlsPromise: Promise<void> | null = null;
function loadHls(): Promise<void> {
  if (window.Hls) return Promise.resolve();
  hlsPromise ||= new Promise<void>((ok, fail) => {
    const s = document.createElement('script');
    s.src = __HLS_URL__;
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.onload = () => ok();
    s.onerror = () => { hlsPromise = null; fail(new Error('hls.js failed to load')); };
    document.head.appendChild(s);
  });
  return hlsPromise;
}

export interface PlayerProps {
  episodeKey: string;
  caption: string;
  color: string;
  durationS: number;
  startAt?: number;
  /** Anonymous viewer code shown faintly on full episodes. Empty = no watermark (Reels). Never a name or phone number. */
  watermark?: string;
  playlistUrl?: string | null;
  /** full = classic controls under the video; reel = short preview; episode = full-screen reel-style episode player */
  mode?: 'full' | 'reel' | 'episode';
  autoPlay?: boolean;
  onProgress?: (positionS: number) => void;
  onEnded?: () => void;
  onTick?: (positionS: number) => void;
}

export function Player({
  episodeKey, caption, color, durationS, startAt = 0, watermark, playlistUrl, mode = 'full',
  autoPlay = true, onProgress, onEnded, onTick,
}: PlayerProps) {
  const blocked = usePlaybackBlock();
  const settings = useApp((s) => s.settings);
  const [playing, setPlaying] = useState(autoPlay);
  const [pos, setPos] = useState(startAt);
  const [speed, setSpeed] = useState(1);
  const [waiting, setWaiting] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSent = useRef(startAt);
  const ended = useRef(false);
  const posRef = useRef(pos);
  posRef.current = pos;
  const tapRef = useRef<{ t: number; timer: number | null }>({ t: 0, timer: null });
  const [flash, setFlash] = useState<{ side: 'left' | 'right'; key: number } | null>(null);

  const active = playing && !blocked;

  // New episode: reset.
  useEffect(() => {
    setPos(startAt);
    setPlaying(autoPlay);
    lastSent.current = startAt;
    ended.current = false;
  }, [episodeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simulated clock when there is no real video.
  useEffect(() => {
    if (playlistUrl || !active) return;
    const t = window.setInterval(() => {
      setPos((p) => {
        const n = Math.min(durationS, p + 0.25 * speed);
        if (n >= durationS && !ended.current) {
          ended.current = true;
          window.setTimeout(() => { setPlaying(false); onEnded?.(); }, 0);
        }
        return n;
      });
    }, 250);
    return () => window.clearInterval(t);
  }, [active, playlistUrl, speed, durationS, onEnded]);

  // Real video element (when the API returns a signed playlist).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playlistUrl) return;
    let hls: { destroy(): void } | null = null;
    let cancelled = false;
    const attach = () => {
      if (cancelled || !window.Hls?.isSupported()) return;
      const h = new window.Hls();
      h.loadSource(playlistUrl);
      h.attachMedia(v);
      hls = h;
      v.currentTime = startAt;
    };
    if (v.canPlayType('application/vnd.apple.mpegurl')) { v.src = playlistUrl; v.currentTime = startAt; }
    else if (window.Hls) attach();
    else loadHls().then(attach).catch(() => setWaiting(false));
    return () => { cancelled = true; hls?.destroy(); };
  }, [playlistUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playlistUrl) return;
    v.playbackRate = speed;
    if (active) v.play().catch(() => setPlaying(false));
    else v.pause();
  }, [active, speed, playlistUrl]);

  useEffect(() => { onTick?.(pos); }, [pos, onTick]);

  // Heartbeat: every 30 s of watching, and on pause.
  useEffect(() => {
    if (Math.abs(pos - lastSent.current) >= 30) {
      lastSent.current = pos;
      onProgress?.(pos);
    }
  }, [pos, onProgress]);
  useEffect(() => {
    if (!active && posRef.current > 1) {
      lastSent.current = posRef.current;
      onProgress?.(posRef.current);
    }
  }, [active, onProgress]);

  const toggle = useCallback(() => {
    if (ended.current) { ended.current = false; setPos(0); setPlaying(true); return; }
    setPlaying((p) => !p);
  }, []);
  const seek = useCallback((to: number) => {
    const n = Math.max(0, Math.min(durationS - 0.5, to));
    ended.current = false;
    setPos(n);
    if (videoRef.current) videoRef.current.currentTime = n;
  }, [durationS]);

  // Keyboard on the full and episode players: Space, ← →.
  useEffect(() => {
    if (mode === 'reel') return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); toggle(); }
      if (e.key === 'ArrowRight') seek(posRef.current + 10);
      if (e.key === 'ArrowLeft') seek(posRef.current - 10);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, toggle, seek]);

  const fullscreen = () => {
    const el = wrapRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else el.webkitRequestFullscreen?.();
  };

  const cycleQuality = () => {
    const order: Quality[] = ['auto', 'saver', 'hd'];
    const next = order[(order.indexOf(settings.quality) + 1) % order.length];
    setState((s) => ({ settings: { ...s.settings, quality: next } }));
  };

  const pct = durationS ? (pos / durationS) * 100 : 0;

  // Episode mode: single tap = play/pause, double tap on the left or right third = back / forward 10 s.
  const onStageTap = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (mode === 'reel') { toggle(); return; }
    if (mode !== 'episode') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const now = Date.now();
    const tap = tapRef.current;
    const side = x < 0.34 || x > 0.66;
    // Centre taps pause/play instantly. Only side taps wait briefly to see if a second tap follows.
    if (!side) { if (tap.timer) window.clearTimeout(tap.timer); tap.timer = null; tap.t = 0; toggle(); return; }
    if (now - tap.t < 300) {
      if (tap.timer) window.clearTimeout(tap.timer);
      tap.timer = null;
      tap.t = 0;
      const side = x < 0.34 ? 'left' : 'right';
      seek(posRef.current + (side === 'left' ? -10 : 10));
      haptic('tap');
      setFlash({ side, key: now });
      return;
    }
    tap.t = now;
    if (tap.timer) window.clearTimeout(tap.timer);
    tap.timer = window.setTimeout(() => { tap.timer = null; toggle(); }, 260);
  };
  const toggleCaptions = () => setState((s) => ({ settings: { ...s.settings, captions: !s.settings.captions } }));
  const nextSpeed = () => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);

  return (
    <div ref={wrapRef} className={`player player-${mode}`} style={{ '--poster': color } as CSSProperties}>
      <div className="player-stage" onClick={mode === 'full' ? undefined : onStageTap}>
        {playlistUrl ? (
          <video
            ref={videoRef}
            className="player-video"
            playsInline
            controls={false}
            disablePictureInPicture
            // @ts-expect-error: non-standard attributes supported by Chrome/Safari
            disableRemotePlayback
            controlsList="nodownload noremoteplayback noplaybackrate"
            onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
            onWaiting={() => setWaiting(true)}
            onPlaying={() => setWaiting(false)}
            onCanPlay={() => setWaiting(false)}
            onEnded={() => { setPlaying(false); onEnded?.(); }}
          />
        ) : (
          <div className="player-frame" aria-label="Video">
            <span className="player-frame-label">9:16 VIDEO · {QUALITY_LABEL[settings.quality]}</span>
            <span className="player-frame-hook">{caption}</span>
          </div>
        )}
        {watermark ? <Watermark text={watermark} /> : null}
        {waiting && active && <span className="buffer-spinner" role="status" aria-label="Loading video" />}
        {settings.captions && active && mode !== 'reel' && playlistUrl && <span className="captions">{caption}</span>}
        {flash && <span key={flash.key} className={'seek-flash ' + flash.side} aria-hidden="true">{flash.side === 'left' ? '−10s' : '+10s'}</span>}
        {!active && !blocked && (
          <button type="button" className="big-play" onClick={(e) => { e.stopPropagation(); toggle(); }} aria-label="Play">
            <Icon name="play" size={34} />
          </button>
        )}
        {blocked && (
          <div className="player-blocked" role="status">
            <Icon name="shield" size={34} />
            <p>{BLOCK_TEXT[blocked]}</p>
          </div>
        )}
      </div>

      {mode === 'reel' ? (
        <div className="reel-progress" aria-hidden="true"><span style={{ transform: `scaleX(${pct / 100})` }} /></div>
      ) : mode === 'episode' ? (
        <div className="ep-controls">
          <div className="ep-chips">
            <button type="button" className="ep-chip" onClick={nextSpeed} aria-label="Playback speed">{speed}x</button>
            <button type="button" className="ep-chip" onClick={cycleQuality} aria-label="Video quality">{QUALITY_LABEL[settings.quality]}</button>
            <button type="button" className={'ep-chip' + (settings.captions ? ' on' : '')} onClick={toggleCaptions} aria-pressed={settings.captions} aria-label="Captions">CC</button>
          </div>
          <div className="seek">
            <span className="time">{clock(pos)}</span>
            <input
              id={`seek-${episodeKey}`}
              type="range"
              min={0}
              max={Math.floor(durationS)}
              step={1}
              value={Math.floor(pos)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Seek"
              style={{ '--pct': `${pct}%` } as CSSProperties}
            />
            <span className="time">{clock(durationS)}</span>
          </div>
        </div>
      ) : (
        <div className="controls">
          <div className="seek">
            <span className="time">{clock(pos)}</span>
            <input
              id={`seek-${episodeKey}`}
              type="range"
              min={0}
              max={Math.floor(durationS)}
              step={1}
              value={Math.floor(pos)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Seek"
              style={{ '--pct': `${pct}%` } as CSSProperties}
            />
            <span className="time">{clock(durationS)}</span>
          </div>
          <div className="control-row">
            <button type="button" className="ctl ctl-play" onClick={toggle} aria-label={active ? 'Pause' : 'Play'}>
              <Icon name={active ? 'pause' : 'play'} size={20} />
            </button>
            <button type="button" className="ctl" onClick={() => seek(pos - 10)} aria-label="Back 10 seconds">−10s</button>
            <button type="button" className="ctl" onClick={() => seek(pos + 10)} aria-label="Forward 10 seconds">+10s</button>
            <span className="grow" />
            <button type="button" className="ctl" onClick={cycleQuality} aria-label="Video quality">{QUALITY_LABEL[settings.quality]}</button>
            <button type="button" className="ctl" onClick={nextSpeed} aria-label="Playback speed">{speed}x</button>
            <button
              type="button"
              className={'ctl' + (settings.captions ? ' on' : '')}
              onClick={toggleCaptions}
              aria-pressed={settings.captions}
              aria-label="Captions"
            >CC</button>
            <button type="button" className="ctl" onClick={fullscreen} aria-label="Full screen"><Icon name="expand" size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
